import * as React from 'react'

import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar'
import { cn } from '@/lib/utils'

const SIDEBAR_STATE_COOKIE = /(?:^|;\s*)sidebar_state=(true|false)/

// Restore the persisted collapse state written by the shadcn SidebarProvider
// (cookie `sidebar_state`). Runs client-side only; the authed layouts render
// <Loading/> during SSR so the sidebar subtree never appears in server HTML,
// which means reading the cookie here cannot cause a hydration mismatch.
function readSidebarDefaultOpen(): boolean {
  if (typeof document === 'undefined') return true
  const match = document.cookie.match(SIDEBAR_STATE_COOKIE)
  return match ? match[1] === 'true' : true
}

function hasStickyHeaderProp(child: React.ReactNode): child is React.ReactElement {
  return React.isValidElement(child) && (child.props as Record<string, unknown>)?.['data-sticky-header'] === true
}

interface DashboardShellProps {
  sidebar: React.ReactNode
  navbar: React.ReactNode
  /**
   * A page-wide notice (the platform-status banner) drawn at the top of the content column, beside the
   * sidebar: the sidebar is fixed to the viewport, so a notice above the whole shell would cover it.
   */
  banner?: React.ReactNode
  children: React.ReactNode
}

/**
 * Shared dashboard shell for the customer and admin portals, built on the shadcn
 * inset sidebar. Replaces the Catalyst `SidebarLayout`.
 *
 * Scroll model (owner-approved, mirrors `components/demo/sidebar-demo-page.tsx`):
 * the provider + inset are height-locked (`h-svh overflow-hidden`) so the page
 * body never scrolls; the mobile navbar stays fixed and page content scrolls
 * inside the white inset card with a hidden inner scrollbar. Because the inset is
 * height-bounded, a page's own `CustomerPageWrapper` (`flex h-full flex-col` →
 * `shrink-0` header + `flex-1 overflow-y-auto` body) finally pins its header.
 *
 * The `data-sticky-header` extraction from the old `SidebarLayout` is preserved
 * for any direct-child consumer that opts in, even though `CustomerPageWrapper`
 * handles its own header/body split internally.
 */
export function DashboardShell({ sidebar, navbar, banner, children }: DashboardShellProps) {
  const [defaultOpen] = React.useState(readSidebarDefaultOpen)

  const childArray = React.Children.toArray(children)
  const stickyHeader = childArray.find(hasStickyHeaderProp)
  const bodyChildren = stickyHeader ? childArray.filter((child) => !hasStickyHeaderProp(child)) : children

  return (
    <SidebarProvider defaultOpen={defaultOpen} className="h-svh overflow-hidden">
      {sidebar}
      <SidebarInset className="h-svh overflow-hidden md:peer-data-[variant=inset]:h-[calc(100svh-1rem)]">
        {navbar}
        {banner}
        {stickyHeader && (
          <div className="sticky top-0 z-40 shrink-0 border-b border-border bg-background/95 backdrop-blur-md">
            <div className="px-4 py-4 sm:px-6 lg:px-8 lg:py-6">{stickyHeader}</div>
          </div>
        )}
        <div
          className={cn(
            'flex min-h-0 flex-1 flex-col overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
            stickyHeader && 'px-4 py-4 sm:px-6 lg:px-8 lg:pt-6',
          )}
        >
          {stickyHeader ? <div className="w-full max-w-none">{bodyChildren}</div> : bodyChildren}
        </div>
      </SidebarInset>
    </SidebarProvider>
  )
}
